<template>
  <div>
    <Dialog :open="openModal" @update:open="emitClose">
      <DialogContent class="sm:max-w-[600px]">
        <div v-if="isLoading">
          <LayupSpinner />
        </div>
        <div v-else>
          <DialogHeader>
            <DialogTitle>Add Debit Order</DialogTitle>
            <DialogDescription>Merchant Billing Debit Order</DialogDescription>
            <DialogDescription v-if="errorMessage" class="text-red-500">
              {{ errorMessage }}
            </DialogDescription>
          </DialogHeader>
          <SectionDivider class="my-4" />
          <form @submit.prevent="onSubmit">
            <div class="grid grid-cols-2 gap-2">
              <div class="col-span-2">
                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="col-span-1">
                    <FormField v-slot="{ componentField }" name="debitOrder.holderName">
                      <FormItem>
                        <FormLabel>Customer Name</FormLabel>
                        <FormControl>
                          <Input type="text" placeholder="Customer Name" v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                  <div class="col-span-1">
                    <FormField v-slot="{ componentField }" name="debitOrder.holderSurname">
                      <FormItem>
                        <FormLabel>Customer Surname</FormLabel>
                        <FormControl>
                          <Input type="text" placeholder="Customer Surname" v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="col-span-1">
                    <FormField v-slot="{ componentField }" name="debitOrder.contact">
                      <FormItem>
                        <FormLabel>Cellphone Number</FormLabel>
                        <FormControl>
                          <Input type="text" placeholder="Cellphone Number." v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                  <div class="col-span-1">
                    <FormField v-slot="{ componentField }" name="debitOrder.identificationNumber">
                      <FormItem>
                        <FormLabel>Company Registration Number</FormLabel>
                        <FormControl>
                          <Input type="text" placeholder="Company Registration Number" v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-4 mb-4">
                  <div class="col-span-1">
                    <div class="space-y-2">
                      <LabelComponent>Select Bank Name</LabelComponent>
                      <Select v-model="selectedBankName">
                        <SelectTrigger>
                          <SelectValue placeholder="Select a bank name" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup v-for="bank in banks" :key="bank.name" :value="bank.name">
                            <SelectItem :value="bank['name'] || ''">{{ bank.name }}</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div class="col-span-1">
                    <FormField v-slot="{ componentField }" name="debitOrder.accountNumber">
                      <FormItem>
                        <FormLabel>Enter Your Account Number</FormLabel>
                        <FormControl>
                          <Input type="text" placeholder="Account Number." v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                </div>

                <div class="col-span-1">
                  <div class="space-y-2">
                    <LabelComponent>Select Account Type</LabelComponent>
                    <Select v-model="selectedAccountType">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a bank name" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup
                          v-for="accountType in accountTypes"
                          :key="accountType.key"
                          :value="accountType.value"
                        >
                          <SelectItem :value="accountType.value">{{ accountType.key }}</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div class="col-span-2 mt-4">
                <FormField v-slot="{ value, handleChange }" type="checkbox" name="termsAndConditions">
                  <FormItem class="flex flex-row items-start gap-x-3 space-y-0">
                    <FormControl>
                      <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                    </FormControl>
                    <div class="space-y-1 leading-none">
                      <FormLabel>
                        You agree to
                        <a
                          href="http://"
                          class="text-brand-700 font-medium underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          LayUp's
                        </a>
                        terms and conditions.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                </FormField>
              </div>
            </div>
            <SectionDivider class="my-4" />
            <div class="flex justify-end gap-4 mt-4 mr-8">
              <Button type="button" variant="outline" @click="emitClose">Cancel</Button>
              <Button class="w-auto" type="submit">Save</Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NetworkError } from '@/errors';
import { initiateDebitOrder } from '@/services/debitOrder';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import * as z from 'zod';
import type { ModelBankResponse } from '@layup/api-sdk';
import { BANK_ACCOUNT_TYPES, type DebitOrderMandateCreateRequest } from '../utils';

const props = defineProps<{
  debitOrder: DebitOrderMandateCreateRequest;
  isCreateOrReplaceDebitOrder: boolean;
  banks: ModelBankResponse[];
}>();

const isLoading = ref(false);
const errorMessage = ref('');
const toast = useToast();

const openModal = ref(false);
const emit = defineEmits(['disable:create', 'get:debit-order']);
const accountTypes = ref(BANK_ACCOUNT_TYPES);
const selectedBankName = ref('');
const selectedAccountType = ref('');

const debitOrderFormSchema = toTypedSchema(
  z.object({
    debitOrder: z.object({
      merchantId: z.string(),
      reference: z.string(),
      holderName: z.string(),
      holderSurname: z.string(),
      contact: z.string(),
      accountType: z.string(),
      accountNumber: z.string(),
      branchCode: z.string(),
      identificationType: z.string(),
      identificationNumber: z.string(),
      bankName: z.string(),
    }),
    termsAndConditions: z.boolean().refine((val) => val === true, {
      message: 'You must accept the terms and conditions',
    }),
  }),
);

watch(
  () => props.isCreateOrReplaceDebitOrder,
  (newValue) => {
    openModal.value = newValue;
  },
  { immediate: true },
);

const form = useForm({
  validationSchema: debitOrderFormSchema,
});

watch(
  () => props.debitOrder,
  (newDebitOrder) => {
    form.setValues({
      debitOrder: {
        merchantId: newDebitOrder.merchantId,
        reference: 'LAYUP-FEES',
        holderName: newDebitOrder.holderName,
        holderSurname: newDebitOrder.holderSurname,
        contact: newDebitOrder.contact,
        accountType: newDebitOrder.accountType,
        accountNumber: newDebitOrder.accountNumber,
        branchCode: newDebitOrder.branchCode,
        identificationType: 'COMPANY_REGISTRATION_NUMBER',
        identificationNumber: newDebitOrder.identificationNumber,
        bankName: newDebitOrder.bankName,
      },
    });
    selectedAccountType.value = newDebitOrder.accountType;
    selectedBankName.value = newDebitOrder.bankName;
    form.validate({ mode: 'silent' });
  },
  { immediate: true, deep: true },
);

const onSubmit = async () => {
  const { values, errors, valid } = await form.validate();
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });
    return;
  }
  if (!valid) {
    errorMessage.value = 'Please fill in all required fields.';
    return;
  }
  isLoading.value = true;
  try {
    const payload = {
      ...values!.debitOrder,
      identificationType: 'COMPANY_REGISTRATION_NUMBER',
      accountType: selectedAccountType.value.toUpperCase(),
      branchCode: props.banks.find((bank) => bank.name === selectedBankName.value)?.branchCode,
      bankName: selectedBankName.value.toUpperCase(),
    };
    await initiateDebitOrder(payload);
    emit('get:debit-order');
    emitClose();
    toast.success('Created Debit Order successfully.', { position: 'top' });
    addMetric('create-debit-order', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
  } catch (error: any) {
    errorMessage.value = 'Something went wrong. Please try again later.';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create-debit-order', {
        status: 'network_error',
        channel: 'merchant',
        method: 'saveMerchant',
      });
    } else {
      createException(error);
      addMetric('create-debit-order', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
    }
    if (error.response.data) {
      errorMessage.value = error.response.data;
    }
  } finally {
    isLoading.value = false;
  }
};

const emitClose = () => {
  openModal.value = false;
  emit('disable:create', false);
};
</script>
